
import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { signIn } from './use-sign-in'
import { useAuthStore } from '@/lib/store/auth'
import { Logger } from '@/lib/utils/logger'

const logger = new Logger('useSignInForm')

export function useSignInForm(role: 'caregiver' | 'specialist') {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (loading) return
    
    setLoading(true)
    
    try {
      logger.info('Attempting sign in', { email, role })
      
      await signIn(email, password, role)
      
      toast({
        title: "Success",
        description: "Signed in successfully",
        duration: 2000, // 2 seconds
      })

      // Navigate based on role
      const path = role === 'specialist' ? '/coach-dashboard' : '/dashboard'
      navigate(path, { replace: true })
    } catch (error) {
      logger.error('Sign in form submission failed', { error })
      
      toast({
        variant: "destructive",
        title: "Sign in failed",
        description: error instanceof Error 
          ? error.message 
          : "An unexpected error occurred. Please try again.",
        duration: 5000, // Show for 5 seconds on mobile
      })
    } finally {
      setLoading(false)
    }
  }, [email, password, role, navigate, toast, loading])

  return {
    email,
    setEmail,
    password,
    setPassword,
    loading,
    handleSubmit
  }
}
